import React, { useMemo, useState } from 'react';
import { Checkbox, Radio, Select } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { REPORT_CONFIG } from '../../constants';
import type { RadUser } from '../../types';

interface AdvancedOptionsProps {
  radUsers: RadUser[];
  allUsers: any[];
  onNotifyPhysician: () => void;
  onCosignChange: (val: string, userData: RadUser) => void;
  onProxyUserChange: (val: string) => void;
  correlated?: string | null;
  diagnosed?: string | null;
  onCorrelatedChange?: (val: string) => void;
  onDiagnosedChange?: (val: string) => void;
}

const AdvancedOptions: React.FC<AdvancedOptionsProps> = ({
  radUsers,
  allUsers,
  onNotifyPhysician,
  onCosignChange,
  onProxyUserChange,
  correlated,
  diagnosed,
  onCorrelatedChange,
  onDiagnosedChange,
}) => {
  const [activeAction, setActiveAction] = useState<string | null>(null);

  const radUserOptions = useMemo(() =>
    radUsers.map(u => ({ label: u.user_fullname, value: u.username, data: u })),
  [radUsers]);

  const allUserOptions = useMemo(() =>
    allUsers.map(u => ({ label: u.user_fullname, value: u.username })),
  [allUsers]);

  return (
    <div className="collapsible-content">
      {REPORT_CONFIG.SHOW_NOTIFY_PHYSICIAN && (
        <div className="advanced-option-item">
          <a className="notify-physician-link" onClick={onNotifyPhysician}>
            <BellOutlined className="notify-bell-icon" />
            Notify Physician
          </a>
        </div>
      )}

      <div className="advanced-option-item">
        <Checkbox onChange={e => setActiveAction(e.target.checked ? 'co_signing' : null)} className="advanced-option-checkbox">
          Report Co-Signing
        </Checkbox>
        {activeAction === 'co_signing' && (
          <Select
            style={{ width: '100%', marginTop: '0.5rem' }}
            onChange={(val, opt: any) => onCosignChange(val, opt?.data)}
            options={radUserOptions}
            placeholder="Select doctor for co-signing"
          />
        )}
      </div>

      {REPORT_CONFIG.SHOW_PROXY_OPTIONS && (
        <>
          <div className="advanced-option-item">
            <Checkbox onChange={e => setActiveAction(e.target.checked ? 'proxy-draft' : null)} className="advanced-option-checkbox">
              Proxy Draft
            </Checkbox>
            {activeAction === 'proxy-draft' && (
              <Select style={{ width: '100%', marginTop: '0.5rem' }} onChange={onProxyUserChange} options={allUserOptions} placeholder="Select user for proxy draft" />
            )}
          </div>

          <div className="advanced-option-item">
            <Checkbox onChange={e => setActiveAction(e.target.checked ? 'proxy-signoff' : null)} className="advanced-option-checkbox">
              Proxy Signoff
            </Checkbox>
            {activeAction === 'proxy-signoff' && (
              <Select style={{ width: '100%', marginTop: '0.5rem' }} onChange={onProxyUserChange} options={radUserOptions} placeholder="Select doctor for proxy signoff" />
            )}
          </div>
        </>
      )}

      {REPORT_CONFIG.SHOW_CORRELATED_DIAGNOSED && (
        <>
          <div className="advanced-option-item advanced-option-radio-group">
            <div className="radio-label">Clinically diagnosed</div>
            <Radio.Group value={diagnosed} onChange={e => onDiagnosedChange?.(e.target.value)}>
              <Radio value="diagnosed">Yes</Radio>
              <Radio value="notdiagnosed">No</Radio>
            </Radio.Group>
          </div>
          <div className="advanced-option-item advanced-option-radio-group">
            <div className="radio-label">Clinically correlated</div>
            <Radio.Group value={correlated} onChange={e => onCorrelatedChange?.(e.target.value)}>
              <Radio value="correlated">Yes</Radio>
              <Radio value="notcorrelated">No</Radio>
            </Radio.Group>
          </div>
        </>
      )}
    </div>
  );
};

export default AdvancedOptions;
